import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

const mapSessionToConversation = (session, adminUser) => {
    const adminId = adminUser?._id || adminUser?.id || "__admin__";
    const adminName = adminUser?.name || "Admin";
    const adminPicture = adminUser?.picture || "https://ui-avatars.com/api/?name=Admin";
    const customerId = session.platformUserId || session.sessionId;
    const customerName = session.platformUserName || session.platformUsername || session.name || session.sessionId;
    const customerPicture = `https://ui-avatars.com/api/?name=${encodeURIComponent(customerName)}`;
    return {
        _id: session.sessionId,
        isGroup: false,
        name: customerName,
        picture: customerPicture,
        users: [
            { _id: adminId, name: adminName, picture: adminPicture },
            { _id: customerId, name: customerName, picture: customerPicture },
        ],
        latestMessage: {
            message: session.lastMessage?.content || "",
            createdAt: session.lastMessage?.timestamp || session.updatedAt || new Date().toISOString(),
        },
        platform: session.platform,
        platformUserId: customerId,
        unreadCount: session.unreadCount || 0,
        tags: Array.isArray(session.tags) ? session.tags : [],
        rawSession: session,
    };
};

const mapBackendMessageToLegacy = (msg, activeConversation, adminUser) => {
    const adminId = adminUser?._id || adminUser?.id || "__admin__";
    const customerId = activeConversation?.platformUserId || activeConversation?.rawSession?.platformUserId || "customer";
    const isAssistant = msg.role === "assistant";
    const sentByAdmin = msg?.metadata?.sentByAdmin;
    const adminNameFromMeta = msg?.metadata?.adminName;
    return {
        _id: msg.id || `${msg.timestamp}-${msg.index || Math.random()}`,
        message: msg.content || "",
        files: msg.files || [],
        createdAt: msg.timestamp,
        metadata: msg.metadata,
        toolCalls: msg.toolCalls || [],
        toolResults: msg.toolResults || [],
        sender: {
            _id: isAssistant ? adminId : customerId,
            name: isAssistant
                ? (sentByAdmin ? (adminNameFromMeta || adminUser?.name || "Admin") : "الذكاء الاصطناعي")
                : (activeConversation?.name || "Customer"),
            picture: isAssistant
                ? (adminUser?.picture || "https://ui-avatars.com/api/?name=Admin")
                : (activeConversation?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(activeConversation?.name || "Customer")}`),
        },
        conversation: {
            _id: activeConversation?._id || msg.sessionId || "conversation",
            isGroup: false,
        },
    };
};

const initialState={
    status:"",
    error:"",
    conversations:[],
    activeConversation:{},
    messages:[],
    notifications:[],
    files:[],
}

export const getConversations = createAsyncThunk("conversation/all",
    async(values, {rejectWithValue, getState})=>{
        try{
            const token = typeof values === 'string' ? values : values?.token;
            const params = typeof values === 'object' ? values?.params : undefined;
            const {data} = await axios.get(`${API_BASE}/admin/messages/sessions`,{
                params: { limit: 200, page: 1, ...(params || {}) },
                headers:{
                    Authorization:`Bearer ${token}`
                }
            });
            const adminUser = getState()?.user?.user || {};
            const sessions = data?.sessions || [];
            return sessions.map((session) => mapSessionToConversation(session, adminUser));
        }catch(error){
            return rejectWithValue(error?.response?.data?.message || error?.message || "Failed to load conversations");
        }
});

export const getConversationMessages = createAsyncThunk("conversation/messages",
    async(values, {rejectWithValue, getState})=>{
        const{token,convo_id} = values;
        try{
            const {data} = await axios.get(
                `${API_BASE}/admin/messages/session/${convo_id}`,
                {
                    params: { limit: 200, offset: 0 },
                    headers:{
                        Authorization:`Bearer ${token}`
                    }
            });
            const state = getState();
            const activeConversation = state?.chat?.activeConversation;
            const adminUser = state?.user?.user || {};
            const backendMessages = data?.messages || [];
            return backendMessages.map((msg) => mapBackendMessageToLegacy(msg, activeConversation, adminUser));
        }catch(error){
            return rejectWithValue(error?.response?.data?.message || error?.message || "Failed to load messages");
        }
});

export const sendMessage = createAsyncThunk("message/send",
    async(values, {rejectWithValue, getState})=>{

        const{token,message,convo_id,files } = values;
        try{
            const {data} = await axios.post(
                `${API_BASE}/admin/messages/session/${convo_id}/send`,{
                    message,
                },
                {
                    headers:{
                        Authorization:`Bearer ${token}`
                    }
            });
            const state = getState();
            const activeConversation = state?.chat?.activeConversation;
            const adminUser = state?.user?.user || {};
            return mapBackendMessageToLegacy(data?.message || {}, activeConversation, adminUser);
        }catch(error){
            return rejectWithValue(error?.response?.data?.message || error?.message || "Failed to send message");
        }
});

export const sendTypedMessage = createAsyncThunk("message/send_typed",
    async(values, {rejectWithValue, getState})=>{
        const{token,convo_id,payload} = values;
        try{
            const {data} = await axios.post(
                `${API_BASE}/admin/messages/session/${convo_id}/send-typed`,
                payload,
                {
                    headers:{
                        Authorization:`Bearer ${token}`
                    }
            });
            const state = getState();
            const activeConversation = state?.chat?.activeConversation;
            const adminUser = state?.user?.user || {};
            return mapBackendMessageToLegacy(data?.message || {}, activeConversation, adminUser);
        }catch(error){
            return rejectWithValue(error?.response?.data?.message || error?.message || "Failed to send message");
        }
});

export const open_create_conversations = createAsyncThunk("conversation/open_create",
    async(values, {rejectWithValue, getState})=>{
        const { receiver_id } = values;
        try{
            const conversations = getState()?.chat?.conversations || [];
            const found = conversations.find((c) => c.platformUserId === receiver_id || c._id === receiver_id);
            return found || null;
        } catch (error) {
            return rejectWithValue(error?.message || "Failed to open conversation");
        }
});

export const createGroupConversation = createAsyncThunk("conversation/create_group",
    async(values, {rejectWithValue})=>{
        return rejectWithValue("Group conversations are not supported in current backend flow");
});

export const chatSlice = createSlice({
    name:"chat",
    initialState,
    reducers:{
        setActiveConversation:(state,action)=>{
            state.activeConversation = action.payload;
        },
        updateMessagesAndConversations:(state,action)=>{
            const payload = action.payload;
            const rawMsg = payload.message || payload;
            const convo = payload.activeConversation || state.activeConversation;
            const adminUser = payload.adminUser || {};
            const isBackendFormat = rawMsg && (rawMsg.content !== undefined || rawMsg.role) && !rawMsg.sender;
            const msgToAdd = isBackendFormat ? mapBackendMessageToLegacy(rawMsg, convo, adminUser) : rawMsg;
            if (convo?._id === payload.conversation?._id || convo?._id === payload.sessionId){
                const nextId = msgToAdd?._id;
                const nextCreatedAt = msgToAdd?.createdAt;
                const nextMessage = msgToAdd?.message;
                const exists = state.messages.some((m) => {
                    if (!m) return false;
                    if (nextId && m._id === nextId) return true;
                    return !!nextCreatedAt && !!nextMessage && m.createdAt === nextCreatedAt && m.message === nextMessage;
                });
                if (!exists && msgToAdd) {
                    state.messages=[...state.messages, msgToAdd];
                }
            }
            const targetConvoId = payload.conversation?._id || payload.sessionId;
            if (!targetConvoId) return;
            let conversation = {
                ...(state.conversations.find((c) => c._id === targetConvoId) || {}),
                _id: targetConvoId,
                latestMessage: msgToAdd,
            };
            let newConvos = [...state.conversations].filter(
                (c)=>c._id !== conversation._id
            );
            newConvos.unshift(conversation);
            state.conversations=newConvos;


        },
        addFiles:(state,action)=>{
            state.files=[...state.files,action.payload];
        },
        clearFiles:(state,action)=>{
            state.files=[];
        },
    removeFileFromFiles:(state,action)=>{
            let index=action.payload;
            let files=[...state.files];
            let fileToRemove = [files[index]];
            state.files = files.filter((file) => !fileToRemove.includes(file));
        },
    },
    extraReducers(builder){
        builder
        .addCase(getConversations.pending,(state,action)=>{
            state.status = "loading";
        })
        .addCase(getConversations.fulfilled,(state,action)=>{
            state.status = "succeeded";
            state.conversations=action.payload;
        })
        .addCase(getConversations.rejected,(state,action)=>{
            state.status = "failed";
            state.error = action.payload;
        })
        .addCase(open_create_conversations.pending,(state,action)=>{
            state.status = "loading";
        })
        .addCase(open_create_conversations.fulfilled,(state,action)=>{
            state.status = "succeeded";
            state.activeConversation=action.payload;
            state.files=[];
        })
        .addCase(open_create_conversations.rejected,(state,action)=>{
            state.status = "failed";
            state.error = action.payload;
        })
        .addCase(getConversationMessages.pending,(state,action)=>{
            state.status = "loading";
        })
        .addCase(getConversationMessages.fulfilled,(state,action)=>{
            state.status = "succeeded";
            state.messages=action.payload;
        })
        .addCase(getConversationMessages.rejected,(state,action)=>{
            state.status = "failed";
            state.error = action.payload;
        }).addCase(sendMessage.pending,(state,action)=>{
            state.status = "loading";
        })
        .addCase(sendMessage.fulfilled,(state,action)=>{
            state.status = "succeeded";
            state.messages=[...state.messages,action.payload];
            let conversation = {...state.activeConversation,
                latestMessage:action.payload,
            };
            let newConvos = [...state.conversations].filter(
                (c)=>c._id !== conversation._id
            );
            newConvos.unshift(conversation);
            state.conversations=newConvos;
        })
        .addCase(sendMessage.rejected,(state,action)=>{
            state.status = "failed";
            state.error = action.payload;
        })
        .addCase(sendTypedMessage.pending,(state,action)=>{
            state.status = "loading";
        })
        .addCase(sendTypedMessage.fulfilled,(state,action)=>{
            state.status = "succeeded";
            state.messages=[...state.messages,action.payload];
            let conversation = {...state.activeConversation,
                latestMessage:action.payload,
            };
            let newConvos = [...state.conversations].filter(
                (c)=>c._id !== conversation._id
            );
            newConvos.unshift(conversation);
            state.conversations=newConvos;
        })
        .addCase(sendTypedMessage.rejected,(state,action)=>{
            state.status = "failed";
            state.error = action.payload;
        });
    }
});

export const {setActiveConversation, updateMessagesAndConversations, addFiles, clearFiles, removeFileFromFiles} = chatSlice.actions;

export default chatSlice.reducer;